import { type VisaListItem, type VisaListQuery } from '@utazzo/shared';
import { UzBadge, UzButton, UzStatusBadge } from '@utazzo/ui';
import { FileSpreadsheet } from 'lucide-react';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useBulkDeleteVisas, useDeleteVisa, useVisasList } from '../use-visas';
import { listVisas } from '../visas.api';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCurrentUser } from '@/features/auth/use-auth';
import { useCountries } from '@/features/cities/use-cities';
import { useTargetCountryFilter } from '@/hooks/use-catalog-geo-filters';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING_APPROVAL', label: 'Pending approval' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'ARCHIVED', label: 'Archived' },
] as const;

const VISA_TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'TOURIST', label: 'Tourist' },
  { value: 'BUSINESS', label: 'Business' },
  { value: 'TRANSIT', label: 'Transit' },
  { value: 'WORK', label: 'Work' },
  { value: 'STUDENT', label: 'Student' },
  { value: 'MEDICAL', label: 'Medical' },
  { value: 'DIPLOMATIC', label: 'Diplomatic' },
] as const;

const ENTRY_TYPE_OPTIONS = [
  { value: '', label: 'All entry types' },
  { value: 'SINGLE', label: 'Single entry' },
  { value: 'DOUBLE', label: 'Double entry' },
  { value: 'MULTIPLE', label: 'Multiple entry' },
] as const;

const APPROVAL_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'approved', label: 'Approved only' },
  { value: 'pending', label: 'Not approved' },
] as const;

const ARCHIVED_OPTIONS = [
  { value: '', label: 'Active only' },
  { value: 'archived', label: 'Include archived' },
] as const;

const MIN_SEARCH_LENGTH = 3;

const FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'visaType',
    label: 'Visa type',
    options: VISA_TYPE_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-48',
  },
  {
    id: 'entryType',
    label: 'Entry',
    options: ENTRY_TYPE_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-44',
  },
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-44',
  },
  {
    id: 'approval',
    label: 'Approval',
    options: APPROVAL_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-40',
  },
  {
    id: 'archived',
    label: 'Archived',
    options: ARCHIVED_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-44',
  },
];

const VISA_TYPE_LABEL: Record<VisaListItem['visaType'], string> = {
  TOURIST: 'Tourist',
  BUSINESS: 'Business',
  TRANSIT: 'Transit',
  WORK: 'Work',
  STUDENT: 'Student',
  MEDICAL: 'Medical',
  DIPLOMATIC: 'Diplomatic',
};

const ENTRY_TYPE_LABEL: Record<VisaListItem['entryType'], string> = {
  SINGLE: 'Single',
  DOUBLE: 'Double',
  MULTIPLE: 'Multiple',
};

const mapStatus = (s: VisaListItem['status']): 'active' | 'inactive' | 'draft' | 'pending' => {
  switch (s) {
    case 'ACTIVE':
      return 'active';
    case 'DRAFT':
      return 'draft';
    case 'PENDING_APPROVAL':
      return 'pending';
    default:
      return 'inactive';
  }
};

export const VisasListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canDelete = me?.role === 'ADMIN';
  const deleteVisa = useDeleteVisa();
  const bulkDeleteMut = useBulkDeleteVisas();
  const navigate = useNavigate();

  // A visa is sold FOR a country and has no city, so Target country is this
  // list's geography filter (server-side `targetCountryId`).
  const { data: countries } = useCountries();
  const targetCountryFilter = useTargetCountryFilter(countries);
  const filters = useMemo(() => [targetCountryFilter, ...FILTERS], [targetCountryFilter]);

  const columns: ListScreenColumns<VisaListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<VisaListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'name',
        header: 'Visa',
        sortable: true,
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.name}</span>
            {row.legacyCode && <span className="text-xs text-zinc-500">{row.legacyCode}</span>}
          </div>
        ),
      },
      {
        id: 'target',
        header: 'Target country',
        cell: (row) => <span className="text-sm text-zinc-900">{row.targetCountryName}</span>,
      },
      {
        id: 'visaType',
        header: 'Type',
        sortable: true,
        width: '120px',
        cell: (row) => <UzBadge tone="secondary">{VISA_TYPE_LABEL[row.visaType]}</UzBadge>,
      },
      {
        id: 'entryType',
        header: 'Entry',
        width: '110px',
        cell: (row) => (
          <span className="text-xs tracking-wide text-zinc-600 uppercase">
            {ENTRY_TYPE_LABEL[row.entryType]}
          </span>
        ),
      },
      {
        id: 'validity',
        header: 'Validity / stay',
        width: '160px',
        cell: (row) => (
          <div className="flex flex-col text-sm">
            <span className="text-zinc-900">{row.validityMonths} months valid</span>
            <span className="text-xs text-zinc-500">{row.maxStayDays} day stay</span>
          </div>
        ),
      },
      {
        id: 'vendor',
        header: 'Vendor',
        cell: (row) => (
          <span className="text-sm text-zinc-900">
            {row.vendorSupplierName ?? <span className="text-zinc-400">-</span>}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        width: '140px',
        cell: (row) => (
          <div className="flex flex-col gap-1">
            <UzStatusBadge status={mapStatus(row.status)} />
            {row.isApprovedByAdmin && (
              <span className="text-[10px] tracking-wide text-emerald-600 uppercase">
                Admin approved
              </span>
            )}
          </div>
        ),
      },
    ];
    return cols;
  };

  return (
    <ListScreen<VisaListItem, VisaListQuery>
      title="Visas"
      description="Visa master. Tiered urgency pricing (Standard / Express) per visa. Surfaced in itinerary VAS pickers."
      useList={useVisasList}
      defaultSort={{ by: 'createdAt', dir: 'desc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const status = state.filters.status;
        const visaType = state.filters.visaType;
        const entryType = state.filters.entryType;
        const approval = state.filters.approval;
        const archived = state.filters.archived;
        const targetCountryId = state.filters.targetCountryId;
        return {
          ...base,
          ...(status ? { status: status as VisaListQuery['status'] } : {}),
          ...(targetCountryId ? { targetCountryId } : {}),
          ...(visaType ? { visaType: visaType as VisaListQuery['visaType'] } : {}),
          ...(entryType ? { entryType: entryType as VisaListQuery['entryType'] } : {}),
          ...(approval === 'approved' ? { isApprovedByAdmin: true } : {}),
          ...(approval === 'pending' ? { isApprovedByAdmin: false } : {}),
          ...(archived === 'archived' ? { includeDeleted: true } : {}),
        };
      }}
      columns={columns}
      filters={filters}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add visa', onClick: () => navigate('/visas/new') }}
      headerExtra={
        <UzButton variant="outline" size="sm" onClick={() => navigate('/visas/imports')}>
          <FileSpreadsheet className="mr-1.5 h-4 w-4" />
          Bulk import
        </UzButton>
      }
      onRowClick={(row) => navigate(`/visas/${row.id}/edit`)}
      rowActions={{
        edit: false,
        delete: {
          onDelete: (row) => deleteVisa.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this visa?',
            description: `${row.name} will be soft-deleted (along with its pricing tiers).`,
          }),
        },
      }}
      bulkActions={{
        delete: {
          onDelete: async (rows) => {
            await bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
          },
          confirm: (rows) => ({
            title: `Delete ${rows.length} visa${rows.length === 1 ? '' : 's'}?`,
            description: 'These visas will be soft-deleted along with their pricing tiers.',
          }),
        },
      }}
      export={{
        fetchPage: (q) => listVisas(q),
        filename: 'visas',
        csv: [
          { header: 'Name', value: (r) => r.name },
          { header: 'Code', value: (r) => r.legacyCode ?? '' },
          { header: 'Target country', value: (r) => r.targetCountryName },
          { header: 'Visa type', value: (r) => VISA_TYPE_LABEL[r.visaType] },
          { header: 'Entry type', value: (r) => ENTRY_TYPE_LABEL[r.entryType] },
          { header: 'Validity months', value: (r) => r.validityMonths },
          { header: 'Max stay days', value: (r) => r.maxStayDays },
          { header: 'Vendor', value: (r) => r.vendorSupplierName ?? '' },
          { header: 'Status', value: (r) => r.status },
        ],
      }}
      copy={{
        searchPlaceholder: 'Search by name, code, target country or vendor (min 3 characters)',
        emptyTitle: 'No visas match these filters',
        emptyDescription: 'Adjust filters or clear the search box.',
        errorTitle: 'Failed to load visas',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
